import { FieldTypes } from 'constants/field-types-enum';
import { useLocale } from 'hooks/locale';
import { Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';

import {
  DatePicker,
  Input,
  InputGroup,
  MultipleSelect,
  PhoneNumberPicker,
  Select,
} from 'components/FormComponents';

import { selectCandidateApplication } from 'api/candidate-application-slice';

import styles from './CustomFields.module.scss';

const slugify = text =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

export const CustomFields = ({ fields, register, control, watch = () => {}, errors = {} }) => {
  const {
    application: { locale },
  } = useSelector(selectCandidateApplication);

  const { dateHyphenFormat } = useLocale(locale);
  const requiredRule = field => (field.required ? 'required' : false);
  const renderField = field => {
    switch (field.type) {
      case FieldTypes.FreeText:
        return (
          <Input
            name={`custom_fields.${field.id}`}
            register={register}
            validators={{ required: requiredRule(field) }}
            placeholder="Enter your answer"
            type={field?.options?.type ? field.options.type : 'text'}
            error={errors?.custom_fields?.[field.id]?.type}
            dataTestId={`custom-field-${slugify(field.text)}`}
          />
        );
      case FieldTypes.MultipleChoice:
        return (
          <Controller
            control={control}
            name={`custom_fields.${field.id}`}
            rules={{ required: requiredRule(field) }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <MultipleSelect
                value={value}
                onChange={onChange}
                placeholder="Select..."
                error={error?.message}>
                {field.options?.options.map(option => (
                  <MultipleSelect.Item
                    value={option.value}
                    active={watch(`custom_fields.${field.id}`)?.includes(option.value)}>
                    {option.value}
                  </MultipleSelect.Item>
                ))}
              </MultipleSelect>
            )}
          />
        );
      case FieldTypes.YesNo:
        return (
          <Select
            name={`custom_fields.${field.id}`}
            register={register}
            validators={{ required: requiredRule(field) }}
            value={watch(`custom_fields.${field.id}`)}
            error={errors?.custom_fields?.[field.id]?.type}
            useDefault>
            <Select.Item value="Yes">Yes</Select.Item>
            <Select.Item value="No">No</Select.Item>
          </Select>
        );
      case FieldTypes.Date:
        return (
          <Controller
            control={control}
            name={`custom_fields.${field.id}`}
            rules={{ required: requiredRule(field) }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <DatePicker
                outputFormat="YYYY-MM-DD"
                format={dateHyphenFormat}
                value={value}
                onChange={onChange}
                useDefaultToday={!!field.options?.useDefault}
                error={error}
              />
            )}
          />
        );
      case FieldTypes.PhoneNumber:
        return (
          <Controller
            key={field.id}
            control={control}
            name={`custom_fields.${field.id}`}
            rules={{ required: requiredRule(field) }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <PhoneNumberPicker
                value={value}
                className={styles.phone}
                onChange={onChange}
                error={error}
              />
            )}
          />
        );
    }
  };

  return (
    <div className="u-width-100">
      {fields?.map(field => (
        <InputGroup title={field.required ? field.text + ' *' : field.text}>
          {renderField(field)}
        </InputGroup>
      ))}
    </div>
  );
};
